import {forwardRef, memo, useCallback} from "react";

import {type CalendarDay} from "./calendar-days";

interface CalendarDayItemProps {
    day: CalendarDay;
    index: number;
    isDisabled: boolean;
    additionalClassName: string | null | undefined;
    onDayClick: (day: CalendarDay, element: HTMLDivElement) => void;
    onMouseEnter: (day: CalendarDay, isDisabled: boolean, index: number, element: HTMLDivElement) => void;
    hideOtherMonthDaysContent?: boolean;
}

export const CalendarDayItem = memo(
    forwardRef<HTMLDivElement, CalendarDayItemProps>(function CalendarDayItem(
        {day, index, isDisabled, additionalClassName, onDayClick, onMouseEnter, hideOtherMonthDaysContent},
        ref,
    ) {
        const wrapperRef = useCallback(
            (node: HTMLDivElement | null) => {
                if (typeof ref === "function") {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref],
        );

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                const wrapper = e.currentTarget.parentElement as HTMLDivElement;
                onDayClick(day, wrapper);
            },
            [onDayClick, day],
        );

        const handleMouseEnter = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                onMouseEnter(day, isDisabled, index, e.currentTarget);
            },
            [onMouseEnter, day, isDisabled, index],
        );

        const isHiddenOtherMonth = Boolean(hideOtherMonthDaysContent && !day.isCurrentMonth);

        return (
            <div
                ref={wrapperRef}
                className="daterange-picker-field__day-wrapper"
                style={isHiddenOtherMonth ? {height: 0, pointerEvents: "none"} : undefined}
                onMouseEnter={handleMouseEnter}
            >
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={isDisabled}
                    className={`daterange-picker-field__day ${
                        !day.isCurrentMonth ? "daterange-picker-field__day--other-month" : ""
                    } ${day.isToday && day.isCurrentMonth ? "daterange-picker-field__day--today" : ""} ${
                        day.isStartDate ? "daterange-picker-field__day--start" : ""
                    } ${day.isEndDate ? "daterange-picker-field__day--end" : ""} ${
                        day.isInRange ? "daterange-picker-field__day--in-range" : ""
                    } ${day.isInHoverRange ? "daterange-picker-field__day--hover-range" : ""} ${
                        day.isHoverBefore ? "daterange-picker-field__day--hover-before" : ""
                    } ${additionalClassName ? `daterange-picker-field__day--${additionalClassName}` : ""}`}
                >
                    {hideOtherMonthDaysContent && !day.isCurrentMonth ? null : day.day}
                </button>
            </div>
        );
    }),
    (prevProps, nextProps) => {
        // Compare day object
        const prevDay = prevProps.day;
        const nextDay = nextProps.day;

        // Compare date by getTime() instead of reference
        if (prevDay.date.getTime() !== nextDay.date.getTime()) return false;

        // Compare other day properties
        if (prevDay.day !== nextDay.day) return false;
        if (prevDay.isCurrentMonth !== nextDay.isCurrentMonth) return false;
        if (prevDay.isToday !== nextDay.isToday) return false;
        if (prevDay.isStartDate !== nextDay.isStartDate) return false;
        if (prevDay.isEndDate !== nextDay.isEndDate) return false;
        if (prevDay.isInRange !== nextDay.isInRange) return false;
        if (prevDay.isInHoverRange !== nextDay.isInHoverRange) return false;
        if (prevDay.isHoverBefore !== nextDay.isHoverBefore) return false;
        if (prevDay.isDisabled !== nextDay.isDisabled) return false;

        // Compare other props
        if (prevProps.index !== nextProps.index) return false;
        if (prevProps.isDisabled !== nextProps.isDisabled) return false;
        if (prevProps.additionalClassName !== nextProps.additionalClassName) return false;
        if (prevProps.hideOtherMonthDaysContent !== nextProps.hideOtherMonthDaysContent) return false;
        if (prevProps.onDayClick !== nextProps.onDayClick) return false;
        return prevProps.onMouseEnter === nextProps.onMouseEnter;
    },
);
